import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { FCX } from '@datassential/platform-ui-lib';
import { ISocialPost } from '@models/SocialPosts';
import SocialPostCard from './SocialPostCard';
import './ExpandableSocialPostCard.scss';

interface ExpandableSocialPostCardProps {
    post: ISocialPost;
    scrollRef?: React.RefObject<HTMLDivElement>;
    isDark?: boolean;
    /** Highlighted in the post text, as on `SocialPostCard`. */
    word?: string;
    association?: string;
    testIdPrefix?: string;
    className?: string;
    expandedClassName?: string;
}

const ExpandableSocialPostCard: FCX<ExpandableSocialPostCardProps> = ({
    post,
    scrollRef,
    isDark,
    word,
    association,
    testIdPrefix,
    className,
    expandedClassName,
}) => {
    const cardRef = useRef<HTMLDivElement | null>(null);
    const textRef = useRef<HTMLDivElement | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [rect, setRect] = useState<DOMRect | null>(null);
    const [fontFamily, setFontFamily] = useState<string | undefined>(undefined);
    const isScrollingRef = useRef(false);

    const handleMouseEnter = useCallback(() => {
        if (isScrollingRef.current) return;
        if (cardRef?.current && textRef?.current) {
            const isOverflowing = textRef.current.scrollHeight > textRef.current.clientHeight;
            if (isOverflowing) {
                setRect(cardRef.current.getBoundingClientRect());
                setFontFamily(window.getComputedStyle(cardRef.current).fontFamily);
                setIsExpanded(true);
                setIsAnimating(false);
            }
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsExpanded(false);
        setIsAnimating(false);
        isScrollingRef.current = false;
    }, []);

    useEffect(() => {
        if (!isExpanded) return;

        const frameId = requestAnimationFrame(() => {
            requestAnimationFrame(() => setIsAnimating(true));
        });

        const scrollEl = scrollRef?.current;
        const onScroll = () => {
            setIsExpanded(false);
            setIsAnimating(false);
            isScrollingRef.current = true;
        };

        if (scrollEl) {
            scrollEl.addEventListener('scroll', onScroll);
        }
        return () => {
            cancelAnimationFrame(frameId);
            if (scrollEl) {
                scrollEl.removeEventListener('scroll', onScroll);
            }
        };
    }, [isExpanded, scrollRef]);

    return (
        <div
            ref={cardRef}
            className={cn('ExpandableSocialPostCard', className)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className={cn('ExpandableSocialPostCard__inner', className && `${className}-inner`)}>
                {!!post.url && (
                    <a
                        href={post.url}
                        target="_blank"
                        rel="noreferrer"
                        className="ExpandableSocialPostCard__link"
                    >
                        {post.url}
                    </a>
                )}
                <SocialPostCard
                    post={post}
                    isDark={isDark}
                    word={word}
                    association={association}
                    textRef={textRef}
                    testIdPrefix={testIdPrefix}
                />
            </div>
            {isExpanded && rect && createPortal(
                <div
                    className={cn('ExpandableSocialPostCard__portal', expandedClassName)}
                    style={{
                        position: 'fixed',
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        zIndex: 1000,
                        pointerEvents: 'none',
                        fontFamily,
                    }}
                >
                    <div
                        className={cn(
                            'ExpandableSocialPostCard__inner is-expanded',
                            className && `${className}-inner is-expanded`,
                            isAnimating && 'is-animating',
                        )}
                        style={{ '--card-height': `${rect.height}px` } as React.CSSProperties}
                    >
                        <SocialPostCard
                            post={post}
                            isDark={isDark}
                            word={word}
                            association={association}
                            testIdPrefix={testIdPrefix}
                        />
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default ExpandableSocialPostCard;
